import React from 'react'

function isNonNullable<T>(value: T): value is NonNullable<T> {
    return typeof value !== 'undefined' && value !== null
}

/**
 * Wrapper around React.useContext.
 * Accepts context of nullable value and returns not nullable value or throws exception if value is nullable.
 * @returns Not nullable context value.
 */
export function useDefinedContextValue<T>(context: React.Context<T | undefined>, displayName?: string): NonNullable<T>
/**
 * Wrapper around React.useContext.
 * Accepts context of nullable value and returns not nullable value or throws exception if value is nullable.
 * @returns Not nullable context value.
 */
export function useDefinedContextValue<T>(context: React.Context<T | null>, displayName?: string): NonNullable<T>
/**
 * Wrapper around React.useContext.
 * Accepts context of nullable value and returns not nullable value or throws exception if value is nullable.
 * @returns Not nullable context value.
 */
export function useDefinedContextValue<T>(
    context: React.Context<T | null | undefined>,
    displayName?: string
): NonNullable<T> {
    const state = React.useContext(context)

    if (isNonNullable(state)) {
        return state
    }

    throw new Error(
        `Trying to use "${
            context.displayName ?? displayName ?? 'Unknown'
        }" context before its value was set by provider`
    )
}

/**
 * Creates hook which returns not nullable value of "context".
 *
 * This function can be used to create alias for "useDefinedContextValue(context)".
 * @returns Hook which can be used to get not nullable value of "context".
 */
export function definedContextValueHookFactory<T>(
    context: React.Context<T | undefined>,
    displayName?: string
): () => NonNullable<T>
/**
 * Creates hook which returns not nullable value of "context".
 *
 * This function can be used to create alias for "useDefinedContextValue(context)".
 * @returns Hook which can be used to get not nullable value of "context".
 */
export function definedContextValueHookFactory<T>(
    context: React.Context<T | null>,
    displayName?: string
): () => NonNullable<T>
/**
 * Creates hook which returns not nullable value of "context".
 *
 * This function can be used to create alias for "useDefinedContextValue(context)".
 * @returns Hook which can be used to get not nullable value of "context".
 */
export function definedContextValueHookFactory<T>(
    context: React.Context<T | null | undefined>,
    displayName?: string
): () => NonNullable<T> {
    return () => useDefinedContextValue(context, displayName)
}
